import dayjs from 'dayjs';
import Controlbar from '../controls/Controlbar';
import NavArrows from '../controls/NavArrows';
import { FullScreen, useFullScreenHandle } from '../hooks/useFullScreen';
import { useArrowKeyNav } from '../hooks/useArrowKeyNav';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { fetchFile } from '../Utilities';
import { isMobile } from 'react-device-detect';

export default function AccessImageCard({ elem, imgSrc, isLoaded, vehicleLabel, onLoaded, onFailed, t, allEvents, currentIndex, baseUrl, fallbackUrl, getVehicleLabel: getLabel, direction }) {
    
    const [zoom, setZoom] = useState(1)
    const [fsIndex, setFsIndex] = useState(currentIndex)

    // in fullscreen: show navigated event; otherwise show own
    const fullscreenHandle = useFullScreenHandle()
    const fsEvent = fullscreenHandle.active ? allEvents[fsIndex] : null
    const displaySrc = fsEvent?.bestImageName ? `${baseUrl}${fsEvent.bestImageName}` : (fsEvent ? fallbackUrl : imgSrc)
    const displayEvent = fsEvent ?? elem
    const displayLabel = fsEvent ? getLabel(fsEvent) : vehicleLabel
    const displayTimestamp = dayjs(displayEvent.timestamp).format(t("date.format.full_date_time"))
    const displayDirection = direction === "entry" ? t("pages.access.entry") : t("pages.access.exit")
    const filename = `${t("pages.access.title")}_${displayDirection}${displayLabel ? `_${displayLabel}` : ''}_${dayjs(displayEvent.timestamp).format('YYYY-MM-DD_HH-mm')}.jpg`
    const hasPrev = fullscreenHandle.active && fsIndex > 0
    const hasNext = fullscreenHandle.active && fsIndex < allEvents.length - 1
    const zoomResetRef = useRef(null)

    useArrowKeyNav({
        enabled: fullscreenHandle.active,
        canPrev: hasPrev,
        canNext: hasNext,
        onPrev: () => { zoomResetRef.current?.(); setFsIndex(prev => prev - 1) },
        onNext: () => { zoomResetRef.current?.(); setFsIndex(prev => prev + 1) },
        onEscape: fullscreenHandle.exit
    })

    function PanningInteractionHandler(instance, state) {
        if (state.scale > 1) {
            instance.setup.panning.disabled = false
        } else {
            instance.setup.panning.disabled = true
        }
    }

    return (
        <Box sx={{
            height: isLoaded ? "auto" : 0,
            overflow: "hidden",
            mb: isLoaded ? 0.5 : 0,
            backgroundColor: isLoaded ? 'white' : 'transparent',
            borderBottomLeftRadius: isLoaded ? 6 : 0,
            borderBottomRightRadius: isLoaded ? 6 : 0
        }}>
            <TransformWrapper
                disabled={!isLoaded}
                disablePadding
                doubleClick={{ disabled: true }}
                wheel={{ disabled: !fullscreenHandle.active }}
                onZoomStop={ref => PanningInteractionHandler(ref.instance, ref.state)}
                onPanning={ref => PanningInteractionHandler(ref.instance, ref.state)}
                onInit={ref => ref.instance.setup.panning.disabled = true}
                onTransformed={ref => setZoom(ref.state.scale)}
            >
                {(utils) => {
                    zoomResetRef.current = utils.resetTransform
                    return (
                        <FullScreen
                            handle={fullscreenHandle}
                            onChange={(active) => {
                                utils.resetTransform()
                                if (active) setFsIndex(currentIndex)
                            }}
                        >
                            <Box className={isMobile ? "" : "fullscreen"} sx={{ position: 'relative' }}>
                                <TransformComponent
                                    wrapperStyle={{ width: '100%', height: '100%' }}
                                    contentStyle={{ width: '100%', height: '100%' }}
                                    wrapperProps={{ onDoubleClick: () => fullscreenHandle.active ? fullscreenHandle.exit() : fullscreenHandle.enter() }}
                                >
                                    <img
                                        src={displaySrc}
                                        alt={displayEvent.bestImageName || "image"}
                                        style={{
                                            display: "flex",
                                            width: "100%",
                                            objectFit: fullscreenHandle.active ? "contain" : "cover",
                                            height: fullscreenHandle.active ? "100%" : "auto",
                                            backgroundColor: fullscreenHandle.active ? "black" : "transparent"
                                        }}
                                        loading="lazy"
                                        ref={img => {
                                            if (img && img.complete && img.naturalWidth > 0) onLoaded(elem.id)
                                        }}
                                        onLoad={() => onLoaded(elem.id)}
                                        onError={() => onFailed(elem.id)}
                                    />
                                </TransformComponent>
                                <NavArrows
                                    hasPrev={hasPrev}
                                    hasNext={hasNext}
                                    onPrev={() => { utils.resetTransform(); setFsIndex(prev => prev - 1) }}
                                    onNext={() => { utils.resetTransform(); setFsIndex(prev => prev + 1) }}
                                />
                                {isLoaded && (
                                    <Controlbar
                                        fullscreenHandle={fullscreenHandle}
                                        zoom={zoom}
                                        zoomUtils={utils}
                                        handleTakeSnapshot={() => fetchFile(displaySrc, filename)}
                                        imageSource={displaySrc}
                                        hasLastModified={false}
                                        hasZoom={isMobile || fullscreenHandle.active}
                                    />
                                )}
                            </Box>
                            {fullscreenHandle.active && (
                                <Box className="Title" sx={{ zIndex: 2 }}>
                                    <Box className="Title-Label">
                                        <Typography variant="body1" className="Title-CameraLabel">
                                            {displayDirection}
                                            {displayLabel ? ` | ${displayLabel}` : ""}
                                            {` | ${displayTimestamp}`}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </FullScreen>
                    )
                }}
            </TransformWrapper>
            {isLoaded && (
                <Typography variant="body1" align="center" sx={{ mt: 0.5, userSelect: "none" }}>
                    {vehicleLabel ? `${vehicleLabel} | ` : ""}
                    {displayTimestamp}
                </Typography>
            )}
        </Box>
    )
}